import React, { useEffect, useState } from 'react';
import { apiFetch } from '../../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import { Lightbulb, RefreshCw, Check, X, Play } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_LABEL = {
  open: { text: 'Offen', color: 'bg-amber-500/20 text-amber-300 border-amber-500/40' },
  planned: { text: 'Eingeplant', color: 'bg-sky-500/20 text-sky-300 border-sky-500/40' },
  done: { text: 'Umgesetzt', color: 'bg-emerald-500/20 text-emerald-300 border-emerald-500/40' },
  dismissed: { text: 'Abgelehnt', color: 'bg-rose-500/20 text-rose-300 border-rose-500/40' },
};

export const TrainingWishesTab = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const qs = statusFilter ? `?status=${encodeURIComponent(statusFilter)}` : '';
      const res = await apiFetch(`${API}/api/training-wishes/admin/aggregate${qs}`);
      const json = await res.json();
      if (!res.ok || !json.success) throw new Error(json.detail || 'Fehler');
      setData(json);
    } catch (e) {
      toast.error(e.message || 'Konnte Aggregation nicht laden');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [statusFilter]);

  const setStatus = async (wishId, newStatus) => {
    try {
      const res = await apiFetch(`${API}/api/training-wishes/admin/${wishId}/status`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus }),
      });
      if (!res.ok) throw new Error('Update fehlgeschlagen');
      toast.success('Status aktualisiert');
      load();
    } catch (e) {
      toast.error(e.message || 'Fehler');
    }
  };

  const clusters = data?.clusters || [];
  const raw = data?.raw || [];
  const total = data?.total_wishes || 0;

  return (
    <Card className="bg-gradient-to-br from-gray-900 to-black border-yellow-600/20">
      <CardHeader>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <CardTitle className="text-white flex items-center gap-2">
            <Lightbulb className="w-5 h-5 text-yellow-400" />
            Schulungswünsche ({total})
          </CardTitle>
          <div className="flex flex-wrap gap-2 items-center">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="bg-black/50 border border-gray-700 text-white text-sm rounded-md px-3 py-1.5"
              data-testid="wishes-status-filter"
            >
              <option value="">Alle Status</option>
              <option value="open">Nur offen</option>
              <option value="planned">Nur eingeplant</option>
              <option value="done">Nur umgesetzt</option>
              <option value="dismissed">Nur abgelehnt</option>
            </select>
            <Button
              variant="outline"
              size="sm"
              onClick={load}
              className="border-gray-700 text-gray-300"
              data-testid="wishes-refresh"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} /> Aktualisieren
            </Button>
          </div>
        </div>
      </CardHeader>

      <CardContent className="space-y-6">
        {loading && !data ? (
          <p className="text-gray-500">Lade Wünsche…</p>
        ) : (
          <>
            {/* Cluster-Karten */}
            <div>
              <h3 className="text-white font-semibold mb-3 text-sm uppercase tracking-wider text-gray-400">
                Priorisierte Themen-Cluster
              </h3>
              {clusters.length === 0 ? (
                <p className="text-gray-500 text-sm">Noch keine Wünsche eingegangen.</p>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {clusters.map((c, idx) => (
                    <div
                      key={c.key}
                      className="rounded-lg border border-yellow-500/20 bg-black/40 p-4"
                      data-testid={`wish-cluster-${c.key}`}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <span className="text-2xl font-black text-yellow-400">#{idx + 1}</span>
                          <span className="text-white font-semibold">{c.label}</span>
                        </div>
                        <Badge className="bg-yellow-500/20 text-yellow-300 border-yellow-500/40 border">
                          {c.count} Anfr.
                        </Badge>
                      </div>
                      {c.upvotes_total > 0 && (
                        <p className="text-xs text-emerald-400 mb-2">+{c.upvotes_total} Upvotes</p>
                      )}
                      <ul className="space-y-1 mt-2">
                        {(c.sample_wishes || []).map((s) => (
                          <li key={s.id} className="text-xs text-gray-400 border-l-2 border-yellow-500/30 pl-2">
                            {'„'}{s.text}{'"'} <span className="text-gray-600">({s.role})</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Alle einzelnen Wünsche */}
            <div>
              <h3 className="text-white font-semibold mb-3 text-sm uppercase tracking-wider text-gray-400">
                Alle Wünsche ({raw.length})
              </h3>
              {raw.length === 0 ? (
                <p className="text-gray-500 text-sm">Nichts zu zeigen.</p>
              ) : (
                <div className="rounded-lg border border-gray-800 overflow-hidden">
                  <div className="max-h-96 overflow-y-auto divide-y divide-gray-800">
                    {raw.map((w) => {
                      const meta = STATUS_LABEL[w.status] || STATUS_LABEL.open;
                      return (
                        <div key={w.id} className="p-3 hover:bg-white/[0.02]" data-testid={`wish-row-${w.id}`}>
                          <div className="flex items-start justify-between gap-3">
                            <div className="flex-1 min-w-0">
                              <p className="text-sm text-white">{w.text}</p>
                              <div className="flex items-center gap-2 mt-1 text-[11px] text-gray-500">
                                <span className={`px-1.5 py-0.5 rounded ${meta.color} border`}>{meta.text}</span>
                                <span>· {w.requester_role}</span>
                                {w.requester_name && <span>· {w.requester_name}</span>}
                                {w.upvotes > 0 && <span className="text-emerald-400">· +{w.upvotes}</span>}
                                {(w.keywords || []).map((k) => (
                                  <span key={k} className="text-yellow-300/70">#{k}</span>
                                ))}
                              </div>
                            </div>
                            <div className="flex gap-1 shrink-0">
                              <Button size="sm" variant="ghost" title="Als eingeplant markieren"
                                onClick={() => setStatus(w.id, 'planned')}
                                className="text-sky-400 hover:bg-sky-500/10 h-8 w-8 p-0" data-testid={`wish-plan-${w.id}`}>
                                <Play className="w-4 h-4" />
                              </Button>
                              <Button size="sm" variant="ghost" title="Als umgesetzt markieren"
                                onClick={() => setStatus(w.id, 'done')}
                                className="text-emerald-400 hover:bg-emerald-500/10 h-8 w-8 p-0" data-testid={`wish-done-${w.id}`}>
                                <Check className="w-4 h-4" />
                              </Button>
                              <Button size="sm" variant="ghost" title="Ablehnen"
                                onClick={() => setStatus(w.id, 'dismissed')}
                                className="text-rose-400 hover:bg-rose-500/10 h-8 w-8 p-0" data-testid={`wish-dismiss-${w.id}`}>
                                <X className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default TrainingWishesTab;
